import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import * as CookieConsent from 'vanilla-cookieconsent'
import 'vanilla-cookieconsent/dist/cookieconsent.css'
import './CookieConsent.css'
import { applyConsent, sharedCookieDomain } from '@/lib/analytics'

const syncConsent = ({ cookie }: { cookie: CookieConsent.CookieValue }) =>
  applyConsent(cookie.categories)

const CookieConsentBanner = () => {
  const { i18n } = useTranslation()

  useEffect(() => {
    CookieConsent.run({
      // Shared with the website, so a visitor answers the banner only once
      cookie: {
        domain: sharedCookieDomain()
      },

      // Bumped when categories change, so everyone is asked again
      revision: 1,

      guiOptions: {
        consentModal: {
          layout: 'box',
          position: 'bottom right'
        },
        preferencesModal: {
          layout: 'box'
        }
      },

      categories: {
        necessary: {
          enabled: true,
          readOnly: true
        },
        analytics: {
          enabled: false,
          autoClear: {
            cookies: [
              { name: /^ph_/ },
              { name: /^_ph/ },
              { name: /^_ga/ }
            ]
          }
        },
        marketing: {
          enabled: false,
          autoClear: {
            cookies: [{ name: /^_gcl/ }, { name: /^_fbp/ }]
          }
        }
      },

      language: {
        default: i18n.language,
        autoDetect: 'document',
        translations: {
          en: {
            consentModal: {
              title: 'We use cookies',
              description:
                'We use cookies to analyze website traffic and measure our ad campaigns. By accepting analytics and marketing cookies, you help us improve our services.',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              showPreferencesBtn: 'Manage preferences'
            },
            preferencesModal: {
              title: 'Cookie preferences',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              savePreferencesBtn: 'Save preferences',
              closeIconLabel: 'Close',
              sections: [
                {
                  title: 'Cookie usage',
                  description:
                    'We use cookies to ensure the basic functionality of the website and to improve your online experience. You can choose for each category to opt-in/out whenever you want.'
                },
                {
                  title: 'Strictly necessary cookies',
                  description:
                    'These cookies are essential for the proper functioning of the website. Without these cookies, the website would not work properly.',
                  linkedCategory: 'necessary'
                },
                {
                  title: 'Analytics cookies',
                  description:
                    'These cookies collect information about how you use the website, which pages you visited and which links you clicked on. We use the data only to improve our services.',
                  linkedCategory: 'analytics'
                },
                {
                  title: 'Marketing cookies',
                  description:
                    'These cookies are used by advertising platforms (Google, Meta, OpenAI) to measure campaign performance and show relevant ads.',
                  linkedCategory: 'marketing'
                }
              ]
            }
          },
          sk: {
            consentModal: {
              title: 'Používame cookies',
              description:
                'Používame cookies na analýzu návštevnosti webu a meranie reklamných kampaní. Prijatím analytických a marketingových cookies nám pomôžete zlepšiť naše služby.',
              acceptAllBtn: 'Prijať všetky',
              acceptNecessaryBtn: 'Odmietnuť všetky',
              showPreferencesBtn: 'Spravovať preferencie'
            },
            preferencesModal: {
              title: 'Nastavenia cookies',
              acceptAllBtn: 'Prijať všetky',
              acceptNecessaryBtn: 'Odmietnuť všetky',
              savePreferencesBtn: 'Uložiť nastavenia',
              closeIconLabel: 'Zavrieť',
              sections: [
                {
                  title: 'Používanie cookies',
                  description:
                    'Používame cookies na zabezpečenie základnej funkcionality webovej stránky a na zlepšenie vášho online zážitku. Pre každú kategóriu si môžete vybrať, či súhlasíte alebo nie.'
                },
                {
                  title: 'Nevyhnutné cookies',
                  description:
                    'Tieto cookies sú nevyhnutné pre správne fungovanie webovej stránky. Bez týchto cookies by webová stránka nefungovala správne.',
                  linkedCategory: 'necessary'
                },
                {
                  title: 'Analytické cookies',
                  description:
                    'Tieto cookies zbierajú informácie o tom, ako používate webovú stránku, ktoré stránky ste navštívili a na ktoré odkazy ste klikli. Údaje využívame iba na zlepšovanie našich služieb.',
                  linkedCategory: 'analytics'
                },
                {
                  title: 'Marketingové cookies',
                  description:
                    'Tieto cookies používajú reklamné platformy (Google, Meta, OpenAI) na meranie účinnosti kampaní a zobrazovanie relevantnej reklamy.',
                  linkedCategory: 'marketing'
                }
              ]
            }
          },
          ua: {
            consentModal: {
              title: 'Ми використовуємо cookies',
              description:
                'Ми використовуємо cookies для аналізу відвідуваності сайту та вимірювання рекламних кампаній. Погодившись на аналітичні та маркетингові cookies, ви допоможете нам покращити наші послуги.',
              acceptAllBtn: 'Прийняти всі',
              acceptNecessaryBtn: 'Відхилити всі',
              showPreferencesBtn: 'Налаштувати'
            },
            preferencesModal: {
              title: 'Налаштування cookies',
              acceptAllBtn: 'Прийняти всі',
              acceptNecessaryBtn: 'Відхилити всі',
              savePreferencesBtn: 'Зберегти налаштування',
              closeIconLabel: 'Закрити',
              sections: [
                {
                  title: 'Використання cookies',
                  description:
                    'Ми використовуємо cookies для забезпечення базової роботи сайту та покращення вашого досвіду. Для кожної категорії ви можете будь-коли надати або відкликати згоду.'
                },
                {
                  title: 'Необхідні cookies',
                  description:
                    'Ці cookies необхідні для правильної роботи сайту. Без них сайт не працюватиме належним чином.',
                  linkedCategory: 'necessary'
                },
                {
                  title: 'Аналітичні cookies',
                  description:
                    'Ці cookies збирають інформацію про те, як ви користуєтеся сайтом, які сторінки відвідали та на які посилання натиснули. Ми використовуємо ці дані лише для покращення наших послуг.',
                  linkedCategory: 'analytics'
                },
                {
                  title: 'Маркетингові cookies',
                  description:
                    'Ці cookies використовують рекламні платформи (Google, Meta, OpenAI) для вимірювання ефективності кампаній і показу релевантної реклами.',
                  linkedCategory: 'marketing'
                }
              ]
            }
          }
        }
      },

      onConsent: syncConsent,
      onChange: syncConsent
    })
  }, [])

  // Update cookie consent language when i18n language changes
  useEffect(() => {
    CookieConsent.setLanguage(i18n.language)
  }, [i18n.language])

  return null
}

export default CookieConsentBanner
